import {Injectable} from '@angular/core';
import {Material, MathUtils, Matrix4, Mesh, MeshBasicMaterial, Object3D, SphereGeometry, Vector2, Vector3} from 'three';
import {SVD} from 'svd-js';
import * as math from 'mathjs';

import {SceneService} from '../../Scene/scene.service';
import {ContainerService} from '../../../Utils/container.service';
import {LoaderService} from '../../Scene/loader.service';
import {RenderService} from '../../Scene/render.service';
import {EditorControlService} from '../../../API/Public/editor-control.service';
import {PictureService} from '../picture.service';
import {ViewComponent} from '../../view.component';
import {OrbitControlsService} from '../../Control/orbit-controls.service';
import {CameraService} from '../../Scene/camera.service';
import {Subscription} from 'rxjs';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {map} from 'rxjs/operators';
import {MouseControlsService} from '../../Control/mouse-controls.service';
import {CanvasControl, CanvasEvent} from '../../Control/controls';
import degToRad = MathUtils.degToRad;
import {ObjectUtilsService} from '../../object-utils.service';

@Injectable({
    providedIn: 'root'
})
export class PictureLandmarkService {

    // get
    private _imageActive = false;
    private _markersVisible = true;

    private _imageGroupName = 'imageLandmarks';
    private _modelGroupName = 'modelLandmarks';
    private _imageMarkers = new Array<Object3D>();
    private _modelMarkers = new Array<Object3D>();
    private _imageMarkerPositions = new Array<Vector3>();
    private _modelMarkerPositions = new Array<Vector3>();

    private _imageLandmarkPosition = new Vector2();
    private _imageLandmarkScale = new Vector3();
    private _modelLandmarkRotation = new Matrix4();

    private _parentIsPlane = true;
    private _subscription: Subscription = null;
    private _smallScreenSize: boolean;

    constructor(private loaderService: LoaderService,
                private sceneService: SceneService,
                private containerService: ContainerService,
                private renderService: RenderService,
                private orbitControlsService: OrbitControlsService,
                private editorControlService: EditorControlService,
                private pictureService: PictureService,
                private cameraService: CameraService,
                private breakpointObserver: BreakpointObserver,
                private objectUtilsService: ObjectUtilsService,
                private mouseControlService: MouseControlsService
    ) {
        this.registerControl();

        this.breakpointObserver.observe([Breakpoints.XSmall])
            .pipe(map(result => result.matches)).subscribe(
            value => this._smallScreenSize = value.valueOf()
        );

    }

    get imageActive(): boolean {
        return this._imageActive;
    }


    get numImageMarkers(): number {
        return this._imageMarkers.length;
    }

    get numModelMarkers(): number {
        return this._modelMarkers.length;
    }

    get markersVisible(): boolean {
        return this._markersVisible;
    }

    /**
     * removes or adds markers to scene
     * (positions are saved)
     */
    set markersVisible(value: boolean) {
        if (this._markersVisible && !value) {
            this.removeMarkersFromParent();
        } else if (!this._markersVisible && value) {
            this.addMarkersToParent();
        }
        this._markersVisible = value;
        this.renderService.render();
    }

// -------------------------------------------------------------------------------------------------------------------------------------

    /**
     * prepares surface view for landmark placement
     * hide human model so that only image is visible
     */
    public initializeLandmarkView(surfaceView: ViewComponent) {
        this._subscription = this.loaderService.loaded.subscribe((loaded) => {
            if (loaded.valueOf() === 0) {
                this._imageActive = false;

                this.cameraService.resetUpRotation();
                this.initializeImage();

                this.renderService.onResize(window.innerWidth, window.innerHeight);
                surfaceView.resize();

                // initialize landmarks controls
                this.orbitControlsService.initControls(this.renderService.renderer);
                this.sceneService.addGroup(this._imageGroupName);

                this.initObjects();
            }
        });
    }

    /**
     * switches from placing image landmarks to placing model landmarks and vice versa
     */
    public switchMode() {
        if (this._imageActive) {
            this._imageActive = false;
            this.switchToModelMode();

        } else {
            this._imageActive = true;
            this.switchToImageMode();
        }
        this.renderService.render();
    }

    /**
     * ends placement of landmarks
     * aligns image and model based on landmarks
     */
    public async finishLandmarkPlacement(): Promise<boolean> {
        if (this._imageMarkers.length === this._modelMarkers.length) {
            this.cameraService.rotateImage = true;

            // ensure both model and image are visible
            this.pictureService.changeOpacity(0.5);

            // align landmarks
            this.cameraService.resetViewOffset();
            this.orbitControlsService.ResetControl();
            this.alignLandmarks();

            // make image landmarks visible and always face the camera
            if (!this._parentIsPlane) {
                this.changeImageMarkersParent();
                this._imageMarkers = this.containerService.imagePlane.children[0].children;
            }
            this.editorControlService.ctrl.next('pan');
            this._subscription.unsubscribe();
            return true;
        }
        return false;
    }

    public abortLandmarkPlacement() {
        this.switchMode();
        this.clearLandmarks();
        this.pictureService.changeOpacity(0.5);

        if (this._parentIsPlane) {
            this.changeImageMarkersParent();
            this._imageMarkers = this.containerService.imagePlane.children[0].children;
        }

        this._imageMarkers = this.containerService.imagePlane.children[0].children;
        this.cameraService.rotateImage = true;

        this.editorControlService.ctrl.next('pan');
        this._subscription.unsubscribe();
    }

    /**
     * removes current landmarks (image/model) from scene
     * doesn't keep their positions
     */
    public clearLandmarks() {
        if (this.imageActive) {
            this._imageMarkers = [];
            this.sceneService.getGroupByName(this._imageGroupName).children = this._imageMarkers;
            this._imageMarkerPositions = [];
        } else {
            this._modelMarkers = [];
            this.sceneService.getGroupByName(this._modelGroupName).children = this._modelMarkers;
            this._modelMarkerPositions = [];
        }
        this.renderService.render();
    }

    /**
     * removes all landmarks (image and model) from scene
     * doesn't keep their positions
     */
    public clearAllLandmarks() {
        this._imageMarkers = [];
        this._modelMarkers = [];
        this._imageMarkerPositions  = [];
        this._modelMarkerPositions = [];
    }

    /**
     * removes markers from scene,
     * but keeps their positions saved
     */
    public removeMarkersFromScene() {
        this.sceneService.getGroupByName(this._imageGroupName).children = [];
        this.sceneService.getGroupByName(this._modelGroupName).children = [];
        this.orbitControlsService.initControls(this.renderService.renderer);
    }

    /**
     * restores alignment of current landmark group
     * after model or image have been transformed
     */
    public realignLandmarks() {
        this.cameraService.resetViewOffset();
        this.orbitControlsService.ResetControl();

        this.restoreModelRotation();
        this.restoreImageScaling();
        this.restoreImagePosition();

        this.renderService.render();
    }

    public resetTransformations() {
        this.cameraService.resetUpRotation();
        this.containerService.imageAligned = false;
        this.containerService.imagePlane.quaternion.copy(this.cameraService.camera.quaternion);
        this.containerService.imagePlane.updateMatrix();

        this.objectUtilsService.resetRotation(this.containerService.renderGroup.children[0]);
        this.objectUtilsService.resetRotation(this.containerService.polyPaintGroup.children[0]);

        this.containerService.imagePlane.children[0].children = [];
        const modelLandmarks = this.sceneService.getGroupByName('modelLandmarks');
        if (modelLandmarks) {
            modelLandmarks.children = [];
        }
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    private registerControl() {
        const control = <CanvasControl> {
            events: new Map([
                [CanvasEvent.Down, event => this.placeLandmark(event)],
            ]),

            pan: true,
            rotate: false,
            zoom: true,

            controlMode: true
        };

        // switch orbit control modes when switch between image and model landmark view
        control.init = () => {
            if (this._imageActive) {
                control.rotate = false;
                control.pan = true;
                control.controlMode = true;
            } else {
                control.rotate = true;
                control.pan = false;
                control.controlMode = false;
            }
        };

        this.editorControlService.registerControl('placeLandmarks', control);

    }

    private initObjects() {
        // remove landmarks from image plane
        if (this._parentIsPlane) {
            this.changeImageMarkersParent();
        }
        this.clearAllLandmarks();

        // reset image scaling
        const scale = this.pictureService.initialScale.clone();
        this.containerService.imagePlane.children[0].scale.set(scale.x, scale.y, scale.z);

        this.updateModelVisibility();
        this.changeInteractionMode('placeLandmarks');
        this.sceneService.getGroupByName(this._imageGroupName).children = this._imageMarkers;
        this.switchMode();

        this.pictureService.changeOpacity(1);
    }

    private placeLandmark(event: MouseEvent) {
        if (event.buttons !== 2 && this.containerService.loadingStatus === 1) {
            const mouse = this.mouseControlService.getCanvasMouse(event);

            let model: Mesh = null;
            if (this.imageActive) {
                model = this.containerService.imagePlane.children[0] as Mesh;
            } else {
                model = <Mesh>this.containerService.renderGroup.children[0];
            }

            const intersects = this.mouseControlService.getMouseModelIntersection(mouse, model);
            if (intersects.length > 0) {
                this.addMarker(intersects[0].point);
            }
        }
    }

    /**
     * adds a marker sphere to either image- or model-landmark group
     * @param point: position of sphere
     */
    public addMarker(point) {
        if (this.imageActive || this._modelMarkers.length < this._imageMarkers.length) {
            const sphere = this.getMarkerSphere(point);

            if (this.imageActive) {
                // add image landmark
                this.sceneService.getGroupByName(this._imageGroupName).add(sphere);
                this._imageMarkerPositions.push(point.clone());
            } else {
                // add model landmark
                this.sceneService.getGroupByName(this._modelGroupName).add(sphere);
                this._modelMarkerPositions.push(point.clone());
            }

            this.renderService.render();
        }
    }

    private restoreImagePosition() {
        const position = this._imageLandmarkPosition;
        this.containerService.imagePlane.children[0].position.x = position.x;
        this.containerService.imagePlane.children[0].position.y = position.y;
    }

    private restoreImageScaling() {
        const scale = this._imageLandmarkScale;
        this.containerService.imagePlane.children[0].scale.set(scale.x, scale.y, scale.z);
    }

    private restoreModelRotation() {
        const rotation = this._modelLandmarkRotation;
        this.containerService.polyPaintGroup.children[0].setRotationFromMatrix(rotation);
        this.containerService.renderGroup.children[0].setRotationFromMatrix(rotation);
    }

    private initializeImage() {
        this.containerService.imageAligned = false;
        this.containerService.imagePlane.quaternion.copy(this.cameraService.camera.quaternion);
        this.containerService.imagePlane.updateMatrix();

        this.sceneService.enableImageMode(true);
        this.orbitControlsService.activateDrag(false);
        this.pictureService.resetImg();
    }

    private updateModelVisibility() {
        this.editorControlService.ctrl.subscribe(value => {
            if (this._imageActive && (value === 'pan' || value === 'placeLandmarks')) {
                this.changeModelVisibility(false);
            }
        });
    }

    private getMarkerSphere(point): Mesh {
        let color = 0xff000f;
        const sphereGeo = new SphereGeometry();

        let scale = new Vector3(0.07, 0.07, 0.07);
        if (this.imageActive) {
            // scale landmark sphere by z position
            scale = scale.divideScalar(point.z);
            color = 0x0000ff;
        }
        if (this._smallScreenSize) {
            scale = scale.multiplyScalar(2);
        }
        sphereGeo.scale(scale.x, scale.y, scale.z);


        // set sphere position to clicked point
        const sphere = new Mesh(sphereGeo, new MeshBasicMaterial({color: color}));
        sphere.position.x = point.x;
        sphere.position.y = point.y;
        sphere.position.z = point.z;
        return sphere;
    }

    private changeImageMarkersParent() {
        const parent = this._parentIsPlane ? this.sceneService.getGroupByName(this._imageGroupName) :
                       this.containerService.imagePlane.children[0];

        while (this._imageMarkers.length > 0) {
            const length = this._imageMarkers.length;

            const child = this._imageMarkers[0];
            child.parent.remove(child);
            parent.attach(child);

            // sometimes array changes in size, sometimes it doesn't though
            // => sizes need to be checked
            if (this._imageMarkers.length === length) {
                this._imageMarkers = this._imageMarkers.slice(1);
            }

        }

        this._imageMarkers = parent.children;
        this._parentIsPlane = !this._parentIsPlane;
    }

    private switchToImageMode() {
        this.cameraService.rotateImage = false;
        this.zoomForImage();

        // hide model landmarks
        const modelGroup = this.sceneService.getGroupByName(this._modelGroupName);
        this._modelMarkers = modelGroup !== undefined ? modelGroup.children : [];
        this.sceneService.removeGroup(this._modelGroupName);

        // add group for image landmarks
        this.sceneService.addGroup(this._imageGroupName);
        this.sceneService.getGroupByName(this._imageGroupName).children = this._imageMarkers;

        this.changeInteractionMode('placeLandmarks');

        // show image hide model
        this.changeModelVisibility(false);
        this.pictureService.changeOpacity(1);

    }

    /**
     * enables image zooming with orbit controls
     */
    private zoomForImage() {
        this.orbitControlsService.ResetControl();
        const pos = this.containerService.imagePlane.position;
        this.cameraService.camera.position.z = pos.z + 1;
        this.orbitControlsService.orbitControls.target = pos.clone();

        this.orbitControlsService.orbitControls.minDistance = 0.1;
        this.orbitControlsService.orbitControls.maxDistance = 8;
        this.orbitControlsService.orbitControls.enableRotate = false;

        this.orbitControlsService.orbitControls.update();
    }

    private switchToModelMode() {
        this.enableModelControls();

        // show model, hide image
        this.changeModelVisibility(true);
        this.pictureService.changeOpacity(0);

        // hide image landmarks
        this._imageMarkers = this.sceneService.getGroupByName(this._imageGroupName).children;
        this.sceneService.removeGroup(this._imageGroupName);

        // add group for model landmarks
        this.sceneService.addGroup(this._modelGroupName);
        this.sceneService.getGroupByName(this._modelGroupName).children = this._modelMarkers;
    }

    /**
     * resets orbit controls values for usage with model
     */
    private enableModelControls() {
        this.orbitControlsService.ResetControl();
        this.renderService.setCameraZ(this.containerService.renderGroup.children[0]);
        this.orbitControlsService.orbitControls.target = new Vector3();
        this.orbitControlsService.orbitControls.enableRotate = true;
        this.orbitControlsService.controlMode = false;
        this.orbitControlsService.orbitControls.update();

        this.cameraService.resetViewOffset();
    }

    /**
     * changes scene interaction mode (control)
     * @param value mode
     */
    private changeInteractionMode(value: string) {
        this.editorControlService.initControl(); // TODO: check why this is necessary every time
        this.editorControlService.ctrl.next(value);
    }

    /**
     * Uses procrustes superimposition algorithm
     * to align model and image landmarks
     */
    private alignLandmarks() {
        // Procrustes Superimposition algorithm

        // 1) shift to origin
        this.shiftLandmarkPositionsToCenter();

        // 2) scale to same size
        this.scaleImageMarkerPositions();

        // 3) get rotation matrix to rotate to same orientation
        const rotation = this.getModelRotation();

        this.applyTransformations(rotation);

    }

    private changeModelVisibility(visible: boolean) {
        const material = <Material>(<Mesh>this.containerService.renderGroup.children[0]).material;

        if (visible) {
            material.opacity = 1;
            material.transparent = false;
        } else {
            material.opacity = 0;
            material.transparent = true;
        }

        material.needsUpdate = true;
        this.renderService.render();

    }

    /**
     * @param points: Array of points in a point cloud
     * @returns centroid of the point cloud
     */
    private getCentroid(points: Array<Vector3>): Vector3 {
        const coordinateSums = new Vector3(0, 0, 0);
        for (let i = 0; i < points.length; ++i) {
            coordinateSums.add(points[i]);
        }

        return coordinateSums.divideScalar(points.length);
    }

    private shiftLandmarkPositionsToCenter() {
        const imageCentroid = this. getCentroid(this._imageMarkerPositions);
        const modelCentroid = this.getCentroid(this._modelMarkerPositions);

        // move positions to center
        for (let i = 0; i < this._imageMarkerPositions.length; ++i) {
            // shift model landmarks
            this._modelMarkerPositions[i].x -= modelCentroid.x;
            this._modelMarkerPositions[i].y -= modelCentroid.y;
            this._modelMarkerPositions[i].z -= modelCentroid.z;

            // shift image landmarks
            this._imageMarkerPositions[i].x -= imageCentroid.x;
            this._imageMarkerPositions[i].y -= imageCentroid.y;
            this._imageMarkerPositions[i].z -= imageCentroid.z;
        }
    }

    // private shiftModelLandmarks() {
    //     const imageCentroid = this.getCentroid(this._imageMarkerPositions);
    //     const modelCentroid = this.getCentroid(this._modelMarkerPositions);
    //     const direction = new Vector3().subVectors(modelCentroid, imageCentroid.multiplyScalar(imageCentroid.z - modelCentroid.z));
    //
    //     // move positions to center
    //     for (let i = 0; i < this._imageMarkerPositions.length; ++i) {
    //         // shift model landmarks
    //         this._modelMarkers[i].position.x -= direction.x;
    //         this._modelMarkers[i].position.y -= direction.y;
    //         this._modelMarkers[i].position.z -= direction.z;
    //     }
    // }


    private getCentroidSize(points: Array<Vector3>): number {
        const centroid = this.getCentroid(points);
        const squareDistances = new Vector3();
        points.forEach(point => {
            const distance = new Vector3().subVectors(point, centroid);
            squareDistances.add(distance.multiply(distance));
        });

        return Math.sqrt((squareDistances.x + squareDistances.y + squareDistances.z) / points.length);
    }

    private scaleImageMarkerPositions() {
        const imageScale = this.getCentroidSize(this._imageMarkerPositions);
        const modelScale = this.getCentroidSize(this._modelMarkerPositions);

        for (let i = 0; i < this._modelMarkers.length; ++i) {
            this._imageMarkerPositions[i].divideScalar(imageScale)    // scale to 1*/
                                         .multiplyScalar(modelScale); // scale to model landmarks
        }
    }

    private getPointMatrix(points: Array<Vector3>): math.Matrix {
        let pointMat = math.matrix(math.zeros([points.length, 3]));
        for (let i = 0; i < points.length; ++i) {
            pointMat = pointMat.set([i, 0],  points[i].x);
            pointMat = pointMat.set([i, 1] , points[i].y);
            pointMat = pointMat.set([i, 2], points[i].z);
        }
        return pointMat;
    }

    private getModelRotation(): Matrix4 {
        const imagePoints = this.getPointMatrix(this._imageMarkerPositions);
        const modelPoints = this.getPointMatrix(this._modelMarkerPositions);

        const crossCovariance = math.multiply(math.transpose(modelPoints), imagePoints);
        const svd = SVD(<number[][]> crossCovariance.toArray()); // Singular Value Decomposition
        const V = math.matrix(svd.v);
        const U = math.matrix(svd.u);

        const det = math.det(math.multiply(V, math.transpose(U)));
        const m = math.matrix([
            [1, 0, 0],
            [0, 1, 0],
            [0, 0, det]
        ]);

        // get rotation matrix from V and U
        const rotation = math.multiply(math.multiply(V, m), math.transpose(U));
        const rotationMatrix =  new Matrix4().set(
            rotation.get([0, 0]), rotation.get([0, 1]), rotation.get([0, 2]),  0,
            rotation.get([1, 0]), rotation.get([1, 1]), rotation.get([1, 2]), 0,
            rotation.get([2, 0]), rotation.get([2, 1]), rotation.get([2, 2]), 0,
            0, 0, 0, 1
        );
        this._modelLandmarkRotation = rotationMatrix.clone();
        return rotationMatrix;

    }

    private applyTransformations(rotation: Matrix4) {
        this.applyModelRotation(rotation);

        let imageCentroid = this.getCentroid(this._imageMarkerPositions);
        const modelCentroid = this.getCentroid(this._modelMarkerPositions);

        const imageScale = this.getVisibleSize(this.getCentroidSize(this._imageMarkerPositions), imageCentroid.z);
        const modelScale = this.getVisibleSize(this.getCentroidSize(this._modelMarkerPositions), modelCentroid.z);
        this.applyImageScaling(imageScale, modelScale);

        imageCentroid = this.getCentroid(this._imageMarkerPositions);
        const zDistance = imageCentroid.z - modelCentroid.z;
        const direction = new Vector3(modelCentroid.x / zDistance - imageCentroid.x,
                                      modelCentroid.y / zDistance - imageCentroid.y, 0);
        this.applyImageTranslation(direction);

        this.applyCameraRotation(rotation);
    }

    /**
     * @returns visible height with respect to camera distance
     * @param size: actual size of object
     * @param z: z coordinate of object
     */
    private getVisibleSize(size: number, z: number): number {
        const fov = degToRad(this.cameraService.camera.fov);
        return size / (2 * Math.tan( fov / 2) * (this.cameraService.camera.position.z - z));
    }

    private applyCameraRotation(rotation: Matrix4) {
        // uncomment to see effect of changing camera up:
        // const axesHelper = new AxesHelper();
        // axesHelper.scale.set(10, 10, 10);
        // this.sceneService.scene.add(axesHelper);

        this.containerService.imageAligned = true;
        this.cameraService.rotateCameraUp(rotation);
        this.cameraService.camera.position.z -= this.orbitControlsService.orbitControls.zoomSpeed;
    }

    private applyModelRotation(rotation: Matrix4) {
        this.containerService.imagePlane.updateMatrix();

        // rotate model and model landmarks
        for (let i = 0; i < this._modelMarkers.length; ++i) {
            this.sceneService.getGroupByName(this._modelGroupName).children[i].applyMatrix4(rotation);
        }
        this.restoreLandmarkPositions();

        this.containerService.polyPaintGroup.children[0].setRotationFromMatrix(rotation);
        this.containerService.renderGroup.children[0].setRotationFromMatrix(rotation);
    }

    private applyImageTranslation(direction) {
        for (let i = 0; i < this._modelMarkers.length; ++i) {
            this._imageMarkers[i].position.x += direction.x;
            this._imageMarkers[i].position.y += direction.y;
            this._imageMarkerPositions[i].x += direction.x;
            this._imageMarkerPositions[i].y += direction.y;
        }

        this.containerService.imagePlane.children[0].position.x += direction.x;
        this.containerService.imagePlane.children[0].position.y += direction.y;
        this.containerService.imagePlane.children.concat(Array.from(this._imageMarkers));

        this._imageLandmarkPosition.setX(this.containerService.imagePlane.children[0].position.x);
        this._imageLandmarkPosition.setY(this.containerService.imagePlane.children[0].position.y);
    }

    private applyImageScaling(imageScale: number, modelScale: number) {
        const z = this._imageMarkerPositions[0].z;
        for (let i = 0; i < this._modelMarkers.length; ++i) {
            this._imageMarkers[i].position.divideScalar(imageScale)    // scale to 1
                                          .multiplyScalar(modelScale); // scale to model landmarks
            this._imageMarkers[i].position.z = z;
            this._imageMarkerPositions[i] = this._imageMarkers[i].position.clone();
        }

        this.containerService.imagePlane.children[0].scale.divideScalar(imageScale)
                                                         .multiplyScalar(modelScale);
        this._imageLandmarkScale = this.containerService.imagePlane.children[0].scale.clone();
    }

    private restoreLandmarkPositions() {
        for (let i = 0; i < this._modelMarkers.length; ++i) {
            this._imageMarkerPositions[i] = this._imageMarkers[i].position.clone();
            this._modelMarkerPositions[i] = this._modelMarkers[i].position.clone();
        }

    }

    private addMarkersToParent() {
        if (this._parentIsPlane) {
            this.containerService.imagePlane.children[0].children = this._imageMarkers;
        } else {
            this.sceneService.getGroupByName(this._imageGroupName).children = this._imageMarkers;
        }
        this.sceneService.getGroupByName(this._modelGroupName).children = this._modelMarkers;
    }


    private removeMarkersFromParent() {
        if (this._parentIsPlane) {
            this._imageMarkers = Array.from(this.containerService.imagePlane.children[0].children);
            this.containerService.imagePlane.children[0].children = [];
        } else {
            this._imageMarkers = Array.from(this.sceneService.getGroupByName(this._imageGroupName).children);
            this.sceneService.getGroupByName(this._imageGroupName).children = [];
        }
        this._modelMarkers = Array.from(this.sceneService.getGroupByName(this._modelGroupName).children);
        this.sceneService.getGroupByName(this._modelGroupName).children = [];
    }

}
